// Shared source of truth for the set of forms Alpaca Intelligence OWNS in the
// audit trail, plus the per-form nav-id helpers.  Imported by BOTH the audit
// page (to scope a single-form log) and AppShell (to build the dynamic
// "Audit Trail Log AI" per-form nav items).  Kept in ONE place so the id
// encoding used to BUILD a nav item and to DECODE it back to a FormName can
// never drift.

/** FormNames that Alpaca Intelligence OWNS (its migrated forms write these audit
 *  rows via IAuditTrailService). The "Audit Trail Log AI" module shows ONLY these;
 *  Data Center → All Forms shows everything ELSE (legacy ALPACA/XAF forms still on
 *  the shared dbo.AuditData). Maintained here as the single split source — add a
 *  form here when it's migrated to AI. Match FormName values EXACTLY as written to
 *  dbo.AuditData. */
export const AI_AUDIT_FORMS: ReadonlySet<string> = new Set([
  // Reporting
  'Custom Reporting','Report Validation','Report Approval','Report Delivery','Report Tracking',
  'Report Signing','Batch Reporting','MOR Reporting','Ad Hoc Reporting','MOR Template Setup',
  'EDD Report Generator','Report Delivery Settings','Project Notes','Case Narrative',
  // Analysis / QC
  'QC Batches','Sample Prep Batches','Result Approval','Result Validation','Batch Data Review',
  'SDMS Result Validation','SDMS Result Entry','Field Data Review 2','MDL Studies',
  // Sampling
  'Sample Registration','Sampling Allocation','Sample Transfer','Field Data Tracking',
  'Field Data Entry','Task Calendar','Scheduled Task Tracking','Sample Container pH Check',
  // Settings / master data
  'Current Language','Time Zones','Country','State','City','Division','Position','Department',
  'Division Department','Glossary','Message Editor','Email Alert Settings','App Config','Form Config',
  'Analysis Department Chain','Payment Terms','Payment Terms Setup',
  // Security
  'Module Access Control','Users','Roles','Navigation',
  // Maintenance
  'Version Log',
  // Module‑CRUD lookups (moduleCrud.ts formName values)
  'Bottle Sharing','Contract Lab',
  'Asbestos Fibers','Assign Dashboard To User','CMDP Sample Category','Category','Container','EDI Sheet Mapping',
  'Fiber Type Values','Fiber Type','Holidays','MSR Data Import Config','MSR Water System','Materials',
  'Method Category','Method','Monthly Summary Repository','Non-Asbestos Fibers','Non-Fibrous Components',
  'Other Components','Permit Library','Permit Setup','Permit Types','Preservative','QC Role',
  'QC Root Role','QC Source','QC Type','Report Category','Report Type','Request Category','Result Range',
  'Sample Category','Storage','Temperature','Test Code','Unconventional Analysis','Units',
]);

/** Nav-id prefix for a single form's AI audit-log leaf (routed by AppShell's
 *  `page.startsWith('at-')` → AuditTrailPage, decoded via aiFormFromNavId). */
export const AI_FORM_NAV_PREFIX = 'at-aiform-';

/** URL-safe slug for a FormName (nav ids become the URL /m/<id>, so no spaces). */
const slug = (name: string): string =>
  name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

/** The per-form audit nav id for a FormName (e.g. "Custom Reporting" → "at-aiform-custom-reporting"). */
export const aiFormNavId = (name: string): string => AI_FORM_NAV_PREFIX + slug(name);

/** Reverse map slug-id → the EXACT FormName.  Built once from AI_AUDIT_FORMS
 *  (the known, static universe), so decoding is exact — no lossy un-slug. */
const _byNavId: Record<string, string> = (() => {
  const m: Record<string, string> = {};
  for (const name of AI_AUDIT_FORMS) {
    const id = aiFormNavId(name);
    // Collision guard: the known set slugs uniquely; if two ever collide, keep
    // the first and leave the second reachable only via the consolidated log.
    if (!m[id]) m[id] = name;
  }
  return m;
})();

/** The exact FormName for a per-form audit nav id, or undefined if not one. */
export const aiFormFromNavId = (id: string): string | undefined => _byNavId[id];
